import {
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  UpdateWorkflowRequest,
  UpdateWorkflowResponse,
  ExecuteWorkflowRequest,
  ExecuteWorkflowResponse,
  GetSpacesResponse,
  GetWorkflowExecutionRequest,
  GetWorkflowExecutionResponse,
  GetWorkflowsRequest,
  GetWorkflowsResponse,
  GetWorkflowExecutionsForSpaceRequest,
  GetWorkflowExecutionsResponse,
  GetCapabilitiesResponse,
  UpdateCapabilityRequest,
  UpdateCapabilityResponse,
} from '@amzn/finspace-service-typescript-client';
import { Api } from './Api';
import { GetAllDimensionsReportingRequest, GetAllDimensionsReportingResponse } from './filters/transformers/request/ProductLineRequestTransformer';

/**
 * Path generators for FinSpaceService API endpoints
 */
export const PATH = {
  createWorkflow: (spaceId: string) => `/space/${spaceId}/workflow`,
  updateWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}`,
  getAllDimensions: '/fae/allDimensions',
  getSpaces: '/spaces',
  getWorkflows: (spaceId: string) => `/space/${spaceId}/workflows`,
  getWorkflowTemplates: (spaceId: string) => `/space/${spaceId}/workflow-templates`,
  getWorkflowExecutions: (spaceId: string) => `/space/${spaceId}/workflow-executions`,
  getWorkflowExecution: (spaceId: string, workflowId: string, workflowExecutionId: string) => `/space/${spaceId}/workflow/${workflowId}/execution/${workflowExecutionId}`,
  getCapabilities: '/capabilities',
  updateCapability: (capabilityId: string) => `/capability/${capabilityId}`,
  deleteWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}`,
  executeWorkflow: (spaceId: string, workflowId: string) => `/space/${spaceId}/workflow/${workflowId}/execute`,
};

/**
 * Create instance of API for FinSpaceService
 */
const API = new Api('FinSpaceService');

/**
 * This object implements all methods presents in FinSpaceService
 */
export const FinSpaceServiceApi = {
  createWorkflow(request: CreateWorkflowRequest): Promise<CreateWorkflowResponse> {
    return API.post(PATH.createWorkflow(request.spaceId!), request);
  },

  updateWorkflow(spaceId: string, workflowId: string, request: UpdateWorkflowRequest): Promise<UpdateWorkflowResponse> {
    return API.put(PATH.updateWorkflow(spaceId, workflowId), request);
  },
  
  getAllDimensions(request: GetAllDimensionsReportingRequest): Promise<GetAllDimensionsReportingResponse> {
    return API.post(PATH.getAllDimensions, request);
  },

  getSpaces(): Promise<GetSpacesResponse> {
    return API.get(PATH.getSpaces);
  },

  getWorkflows(request: GetWorkflowsRequest): Promise<GetWorkflowsResponse> {
    return API.get(PATH.getWorkflows(request.spaceId!));
  },

  getCapabilities(): Promise<GetCapabilitiesResponse> {
    return API.get(PATH.getCapabilities);
  },

  updateCapability(capabilityId: string, request: UpdateCapabilityRequest): Promise<UpdateCapabilityResponse> {
    return API.put(PATH.updateCapability(capabilityId), request);
  },

  getWorkflowTemplates(request: GetWorkflowsRequest): Promise<GetWorkflowsResponse> {
    return API.get(PATH.getWorkflowTemplates(request.spaceId!));
  },

  getAllWorkflowExecutions(request: GetWorkflowExecutionsForSpaceRequest): Promise<GetWorkflowExecutionsResponse> {
    return API.get(PATH.getWorkflowExecutions(request.spaceId!));
  },

  getWorkflowExecution(request: GetWorkflowExecutionRequest): Promise<GetWorkflowExecutionResponse> {
    return API.get(PATH.getWorkflowExecution(request.spaceId!, request.workflowId!, request.workflowExecutionId!));
  },

  deleteWorkflow(spaceId: string, workflowId: string): Promise<void> {
    return API.delete(PATH.deleteWorkflow(spaceId, workflowId));
  },

  executeWorkflow(request: ExecuteWorkflowRequest): Promise<ExecuteWorkflowResponse> {
    return API.post(PATH.executeWorkflow(request.spaceId!, request.workflowId!), request);
  },
};
